import coolBananaPinkImage from "@assets/images/coolBananaPink.png";
import { ChatListItem } from "@features/messaging/components/ChatListItem";
import type { Match } from "@features/messaging/types";
import { deriveMatchStatus, isChatUnread } from "@features/messaging/utils/matchUtils";
import { motion } from "framer-motion";

/**
 * Props interface for the AllMatchesSection component.
 */
interface AllMatchesSectionProps {
  /** Array of all matches to display */
  matches: Match[];
  /** Whether the section is loading */
  isLoading: boolean;
  /** Current user ID */
  currentUserId: string;
  /** Callback when a chat item is clicked */
  onChatClick: (match: Match) => void;
  /** Callback when user opens a match's profile */
  onProfileClick: (userId: string) => void;
}

/**
 * AllMatchesSection component for displaying all matches with existing chats in a vertical list.
 * Shows users with ongoing conversations, sorted by most recent message.
 *
 * Features:
 * - Vertical scrollable list
 * - ChatListItem components for each match with a chat
 * - Loading state with skeleton
 * - Empty state when no chats exist
 * - Only shows matches with existing chats (deriveMatchStatus === "existing" and match.chat present)
 */
export function AllMatchesSection({
  matches,
  isLoading,
  currentUserId,
  onChatClick,
  onProfileClick,
}: AllMatchesSectionProps) {
  const matchesWithChats = matches.filter((match) => {
    if (match.isLocked) {
      return true;
    }
    return deriveMatchStatus(match) === "existing" && !!match.chat;
  });
  const shouldShowEmptyStateHint = matchesWithChats.length < 4;

  return (
    <div className="flex-1 overflow-y-auto bg-white" data-testid="all-matches-section">
      {/* Section Title */}
      {!isLoading && matchesWithChats.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
          <h2 className="text-lg font-semibold font-mono text-gray-900">Messages</h2>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-2 p-4">
          {[1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="flex items-center gap-3 p-4" data-testid="chat-list-skeleton">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3" />
                <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && (
        <div className="flex min-h-full flex-col">
          {/* Matches List */}
          {matchesWithChats.length > 0 && (
            <div className="divide-y divide-gray-100">
              {matchesWithChats.map((match) => {
                const chat = match.chat;
                if (!chat && !match.isLocked) {
                  return null;
                }
                return (
                  <ChatListItem
                    key={match.user.uid}
                    onClick={() => onChatClick(match)}
                    isUnread={isChatUnread(chat, currentUserId)}
                    {...(chat ? { chat } : {})}
                    {...(chat?.last_message_sent_timestamp != null && {
                      lastMessageTime: chat.last_message_sent_timestamp,
                    })}
                    otherUserName={match.user.display_name}
                    otherUserPhoto={match.user.photo_url}
                    otherUserUid={match.user.uid}
                    onProfileClick={onProfileClick}
                    isLocked={match.isLocked}
                  />
                );
              })}
            </div>
          )}

          {/* Empty space engagement hint */}
          {shouldShowEmptyStateHint && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-1 items-center justify-center p-8"
              data-testid="messages-empty-state-hint"
            >
              <div className="text-center">
                <img
                  src={coolBananaPinkImage}
                  alt="Cool banana illustration"
                  className="mx-auto h-44 w-auto max-w-full"
                  data-testid="messages-empty-state-hint-image"
                />
                <h3 className="mt-2 text-xl font-semibold font-mono text-gray-900">Start a conversation</h3>
                <p className="text-gray-600 mb-6">Match with someone and say hello — your chats will appear here.</p>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  data-testid="messages-empty-state-hint-button"
                >
                  Start Swiping
                </button>
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
